import 'react-dates/initialize'
import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Container, Form, Row, Tab, Table, Tabs } from 'react-bootstrap'
import { DateRangePicker } from 'react-dates'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import isAuthenticated from '../../../routes/PrivateRoute'
import { utils, writeFile } from 'xlsx'

const formatMoment = (date, hour, minute, seconds) => {
  return date.set({ hour, minute, seconds }).format()
}

const hasNoResults = (results) => {
  return Object.keys(results).every((key) => results[key].length === 0)
}

const guessTimeZone = () => moment.tz.guess()

const formatDate = (dateString) => {
  const date = new Date(dateString)

  const month = String(date.getUTCMonth() + 1).padStart(2, '0') 
  const day = String(date.getUTCDate()).padStart(2, '0')
  const year = date.getUTCFullYear()

  return `${month}/${day}/${year}`
}

const AlertMessage = ({ variant, message }) => {
  return (
    <Row>
      <Col>
        <Alert variant={variant}>
          {message}
        </Alert>
      </Col>
    </Row>
  )
}

AlertMessage.propTypes = {
  variant: PropTypes.string,
  message: PropTypes.string
}


const Reports = ({ healthSystemId, reportData, getReport, getReportState }) => {
  const [startDate, setStartDate] = useState(moment().subtract(7, 'd'))
  const [endDate, setEndDate] = useState(moment())
  const [focusedInput, setFocusedInput] = useState(null)

  const [activeTab, setActiveTab] = useState('launches')

  const handleChangedFocusedInput = (focusedInput) => {
    setFocusedInput(focusedInput)
  }

  const handleChangeDates = ({ startDate, endDate }) => {
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    getReport({
      startDate: formatMoment(startDate, 0, 0, 0),
      endDate: formatMoment(endDate, 23, 59, 59),
      healthSystemId
    })
  }

  useEffect(() => {
    if (healthSystemId) {
      getReport({
        startDate: formatMoment(startDate, 0, 0, 0),
        endDate: formatMoment(endDate, 23, 59, 59),
        healthSystemId
      })
    } 
  }, [healthSystemId])

  const handleDownloadResults = () => {
    const launches = utils.json_to_sheet(reportData.launches.map((launch) => ({
      'Epic User Id': launch.epicUserId,
      'Epic Version': launch.epicVersion,
      'Access Date': moment.tz(launch.accessDate, timezone).format('MM/DD/YYYY hh:mm a'),
      'Encounter Id': launch.encounterId
    })))

    const sessions = utils.json_to_sheet(reportData.sessions.map((session) => ({
      'Encounter Id': session.encounterId,
      'Encounter Date': moment.utc(session.encounterDate).format('MM/DD/YYYY'),
      'Patient Id': session.patientId,
      'Status': session.status
    })))

    const conditions = utils.json_to_sheet(reportData.conditions.map((condition) => ({
      'Patient Id': condition.patientId,
      'Name': condition.name,
      'Code': condition.code,
      'Code System': condition.codeSystem,
      'Onset Start Date': moment.utc(condition.onsetStartDate).format('MM/DD/YYYY'),
      'Onset End Date': condition.onsetEndDate ? moment.utc(condition.onsetEndDate).format('MM/DD/YYYY') : '',
      'Recorded Date': condition.recordedDate ? moment.utc(condition.recordedDate).format('MM/DD/YYYY') : '',
      'Clinical Status': condition.clinicalStatus,
      'Verification Status': condition.verificationStatus,
    })))

    const medications = utils.json_to_sheet(reportData.medications.map((medication) => ({
      'Patient Id': medication.patientId,
      'Name': medication.name,
      'Code': medication.code,
      'Start Date': medication.startDate ? moment.utc(medication.startDate).format('MM/DD/YYYY') : '',
      'End Date': medication.endDate ? moment.utc(medication.endDate).format('MM/DD/YYYY') : '',
      'Dispense Quantity Value': medication.dispenseQuantityValue,
      'Dispense Quantity Unit': medication.dispenseQuantityUnit,
      'Supply Duration': medication.supplyDuration
    })))

    const observations = utils.json_to_sheet(reportData.observations.map((observation) => ({
      'Patient Id': observation.patientId,
      'Code': observation.code,
      'Code Description': observation.codeDescription,
      'Value': observation.value,
      'Value Unit': observation.valueUnit,
      'Effective Date': observation.effectiveDate ? moment.utc(observation.effectiveDate).format('MM/DD/YYYY') : '',
      'Issued Date': observation.issuedDate ? moment.utc(observation.issuedDate).format('MM/DD/YYYY') : '',
      'Status': observation.status,
      'Data Absent Reason': observation.dataAbsentReason
    })))

    const procedures = utils.json_to_sheet(reportData.procedures.map((procedure) => ({
      'Patient Id': procedure.patientId,
      'Code': procedure.code,
      'Name': procedure.name,
      'Performed Date': procedure.performedDate ? moment.utc(procedure.performedDate).format('MM/DD/YYYY') : '',
      'Status': procedure.status,
      'Outcome': procedure.outcome
    })))

    const sledais = utils.json_to_sheet(reportData.sledais.map((sledai) => ({
      'SLEDAI Id': sledai.sledaiId,
      'Encounter Id': sledai.encounterId,
      'Score': sledai.score,
      'Recorded Date':  formatDate(sledai.recordedDate)
    })))

    const sledaiBreakdown = utils.json_to_sheet(reportData.sledaiBreakdown.map((sledaiBreakdown) => ({
      'SLEDAI Id': sledaiBreakdown.sledaiId,
      'Name': sledaiBreakdown.name,
      'Points': sledaiBreakdown.points
    })))

    const recommendations = utils.json_to_sheet(reportData.recommendations.map((recommendation) => ({
      'Encounter Id': recommendation.encounterId,
      'Name': recommendation.name,
      'Recommendation': recommendation.recommendation,
      'Was Recommended': recommendation.wasRecommended,
      'Was Changed': recommendation.wasChanged
    })))
    
    const orders = utils.json_to_sheet(reportData.orders.map((order) => ({
      'Encounter Id': order.encounterId,
      'Name': order.orderName,
      'Description': order.orderDescription,
      'Type': order.orderType,
      'Was Recommended': order.wasRecommended,
      'Was Changed': order.wasChanged
    })))

    const wb = utils.book_new()
    utils.book_append_sheet(wb, launches, 'Launches')
    utils.book_append_sheet(wb, sessions, 'Sessions')
    utils.book_append_sheet(wb, conditions, 'Conditions')
    utils.book_append_sheet(wb, medications, 'Medications')
    utils.book_append_sheet(wb, observations, 'Observations-Labs')
    utils.book_append_sheet(wb, procedures, 'Procedures')
    utils.book_append_sheet(wb, sledais, 'SLEDAIs')
    utils.book_append_sheet(wb, sledaiBreakdown, 'SLEDAI Breakdowns')
    utils.book_append_sheet(wb, recommendations, 'Recommendations')
    utils.book_append_sheet(wb, orders, 'Orders')

    writeFile(wb, `LA_REPORT_${moment().utc().format('MMDDYYYY')}.xlsx`)
  }

  const timezone = guessTimeZone()

  return (
    <Container fluid className='mx-3'>
      <Row>
        <Col xs={12}>
          <Form id='form' noValidate onSubmit={handleSubmit}>
            <Row>
              <Form.Group as={Col} md={4} xs={6}>
                <Row>
                  <Col xs={12}>
                    <Form.Label>Usage Date Range</Form.Label>
                  </Col>
                  <Col xs={12}>
                    <DateRangePicker
                      startDate={startDate}
                      startDateId="start_date_id" 
                      endDate={endDate}
                      endDateId="end_date_id"
                      onDatesChange={handleChangeDates}
                      focusedInput={focusedInput}
                      onFocusChange={handleChangedFocusedInput}
                      isOutsideRange={(range) => range.isSameOrAfter(moment())}
                      maxDate={moment()}
                      hideKeyboardShortcutsPanel
                      small
                    />
                  </Col>
                </Row>
              </Form.Group>
              <Col xs={4}>
                <Button 
                  style={{ marginTop: '32px' }} 
                  type='submit'
                  disabled={getReportState.loading}
                >
                  Search
                </Button>
                <Button
                  style={{ marginTop: '32px' }}
                  type='button'
                  disabled={hasNoResults(reportData)}
                  className='ml-3'
                  onClick={handleDownloadResults}
                >
                  Download
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      {
        getReportState.loading && (
          <Row>
            <Col>
              Loading Results...
            </Col>
          </Row>
        )
      }
      {
        getReportState.error && (
          <AlertMessage variant='danger' message='Error retrieving reports' />
        )
      }
      {
        getReportState.success && (
          <>
            {
              hasNoResults(reportData) ? (
                <AlertMessage variant='info' message='No data found. Please try adjusting the date range.' />
              ) : (
                <Tabs
                  id="report"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                >
                  <Tab eventKey='launches' title='Launches'>
                    <Table 
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>
                            Epic Log Id
                          </th>
                          <th>
                            Epic User Id
                          </th>
                          <th>
                            Epic Version
                          </th>
                          <th>
                            Access Date
                          </th>
                          <th>
                            Encounter Id
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.launches.map((launch) => (
                            <tr key={`launch-${launch.epicLogId}`}>
                              <td>{launch.epicLogId}</td>
                              <td>{launch.epicUserId}</td>
                              <td>{launch.epicVersion}</td>
                              <td>{moment.tz(launch.accessDate, timezone).format('MM/DD/YYYY hh:mm a')}</td>
                              <td>{launch.encounterId}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='sessions' title='Sessions'>
                    <Table 
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>
                            Encounter Id
                          </th>
                          <th>
                            Encounter Date
                          </th>
                          <th>
                            Patient Id
                          </th>
                          <th>
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.sessions.map((session) => (
                            <tr key={`session-${session.encounterId}`}>
                              <td>{session.encounterId}</td>
                              <td>{moment.utc(session.encounterDate).format('MM/DD/YYYY')}</td>
                              <td>{session.patientId}</td>
                              <td>{session.status}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='conditions' title='Conditions'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Patient Id</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Code System</th>
                          <th>Onset Start Date</th>
                          <th>Onset End Date</th>
                          <th>Recorded Date</th>
                          <th>Clinical Status</th>
                          <th>Verification Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.conditions.map((condition) => (
                            <tr key={`condition-${condition.patientConditionId}`}>
                              <td>{condition.patientId}</td>
                              <td>{condition.name}</td>
                              <td>{condition.code}</td>
                              <td>{condition.codeSystem}</td>
                              <td>{moment.utc(condition.onsetStartDate).format('MM/DD/YYYY')}</td>
                              <td>{condition.onsetEndDate ? moment.utc(condition.onsetEndDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{condition.recordedDate ? moment.utc(condition.recordedDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{condition.clinicalStatus}</td>
                              <td>{condition.verificationStatus}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='medications' title='Medications'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Patient Id</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Dispense Quantity Value</th>
                          <th>Dispense Quantity Unit</th>
                          <th>Supply Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.medications.map((medication) => (
                            <tr key={`medication-${medication.patientMedicationId}`}>
                              <td>{medication.patientId}</td>
                              <td>{medication.name}</td>
                              <td>{medication.code}</td>
                              <td>{medication.startDate ? moment.utc(medication.startDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{medication.endDate ? moment.utc(medication.endDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{medication.dispenseQuantityValue}</td>
                              <td>{medication.dispenseQuantityUnit}</td>
                              <td>{medication.supplyDuration}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='observations' title='Observations'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Patient Id</th>
                          <th>Code</th>
                          <th>Code Description</th>
                          <th>Value</th>
                          <th>Value Unit</th>
                          <th>Effective Date</th>
                          <th>Issued Date</th>
                          <th>Status</th>
                          <th>Data Absent Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.observations.map((observation) => (
                            <tr key={`observation-${observation.patientObservationId}`}>
                              <td>{observation.patientId}</td>
                              <td>{observation.code}</td>
                              <td>{observation.codeDescription}</td>
                              <td>{observation.value}</td>
                              <td>{observation.valueUnit}</td>
                              <td>{observation.effectiveDate ? moment.utc(observation.effectiveDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{observation.issuedDate ? moment.utc(observation.issuedDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{observation.status}</td>
                              <td>{observation.dataAbsentReason}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='procedure' title='Procedures'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Patient Id</th>
                          <th>Code</th>
                          <th>Name</th>
                          <th>Performed Date</th>
                          <th>Status</th>
                          <th>Outcome</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.procedures.map((procedure) => (
                            <tr key={`procedure-${procedure.patientProcedureId}`}>
                              <td>{procedure.patientId}</td>
                              <td>{procedure.code}</td>
                              <td>{procedure.name}</td>
                              <td>{procedure.performedDate ? moment.utc(procedure.performedDate).format('MM/DD/YYYY') : ''}</td>
                              <td>{procedure.status}</td>
                              <td>{procedure.outcome}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='sledais' title="SLEDAIs">
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Sledai Id</th>
                          <th>Encounter Id</th>
                          <th>Score</th>
                          <th>Recorded Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.sledais.map((sledai) => (
                            <tr key={`sledai-${sledai.sledaiId}`}>
                              <td>{sledai.sledaiId}</td>
                              <td>{sledai.encounterId}</td>
                              <td>{sledai.score}</td>
                              <td>{formatDate(sledai.recordedDate)}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='sledaiBreakdowns' title='SLEDAI Breakdowns'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Sledai Id</th>
                          <th>Name</th>
                          <th>Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.sledaiBreakdown.map((sledaiBreakdown, index) => (
                            <tr key={`sledaiBreakdown-row-${index}`}>
                              <td>{sledaiBreakdown.sledaiId}</td>
                              <td>{sledaiBreakdown.name}</td>
                              <td>{sledaiBreakdown.points}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='recommendations' title='Recommendations'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Patient Recommendation Id</th>
                          <th>Encounter Id</th>
                          <th>Name</th>
                          <th>Recommendation</th>
                          <th>Was Recommended</th>
                          <th>Was Changed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.recommendations.map((recommendation) => (
                            <tr key={`recommendation-${recommendation.patientRecommendationId}`}>
                              <td>{recommendation.patientRecommendationId}</td>
                              <td>{recommendation.encounterId}</td>
                              <td>{recommendation.name}</td>
                              <td>{recommendation.recommendation}</td>
                              <td>{recommendation.wasRecommended}</td>
                              <td>{recommendation.wasChanged}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                  <Tab eventKey='orders' title='Orders'>
                    <Table
                      className='mt-2 font-85'
                      striped
                      bordered
                      hover
                      responsive
                    >
                      <thead>
                        <tr>
                          <th>Patient Action Id</th>
                          <th>Encounter Id</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Was Recommended</th>
                          <th>Was Changed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          reportData.orders.map((order) => (
                            <tr key={`order-${order.patientActionId}`}>
                              <td>{order.patientActionId}</td>
                              <td>{order.encounterId}</td>
                              <td>{order.orderName}</td>
                              <td>{order.orderDescription}</td>
                              <td>{order.orderType}</td>
                              <td>{order.wasRecommended}</td>
                              <td>{order.wasChanged}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </Tab>
                </Tabs>
              )
            }
          </>
        )
      }
    </Container>
  )
}

Reports.propTypes = {
  healthSystemId: PropTypes.number,
  reportData: PropTypes.any,
  getReport: PropTypes.func,
  getReportState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
}

export default isAuthenticated(Reports)